import {
  getTreatmentSession,
  isTerminalTreatmentSessionError,
} from "./authorityApi";
import { startTreatmentAuthority } from "./authorityCoordinator";
import {
  RUNTIME_PATIENT_AUTH_REQUIRED,
  TREATMENT_AUTHORITY_RESPONSE_KEY,
  TREATMENT_RUNTIME_POINTER_SCHEMA_VERSION,
  TREATMENT_STORAGE_KEY,
} from "./treatmentApiConstants";
import { refreshPatientAccessToken } from "./authentication/patientAccessRefresh";
import { isRuntimePatientPermissionError } from "./patientRuntimeIdentity";
import { normalizeRuntimeApiBaseUrl } from "./runtimeApiBase";
import { projectRuntimeQuestionnaireQuestions } from "./checkoutContract";
import { isCanonicalConsentQuestion } from "./consentQuestionClassification";
import {
  normalizeRuntimeAuthority,
  pruneResponsesToProjection,
} from "./runtimeAuthority";
import { resumeProgramLabCheckout } from "./resumeProgramLabCheckout";
import { TreatmentRuntimeResumeError } from "./treatmentRuntimeResumeError";
import { askFirstStateHint, settleLaunchPointer } from "./earlierIntake";

export { TreatmentRuntimeResumeError };

const parseStorage = (storage, key) => {
  const raw = storage.getItem(key);
  if (raw == null) return { present: false, value: null };
  try {
    return { present: true, value: JSON.parse(raw) };
  } catch (_error) {
    return { present: true, value: null, invalid: true };
  }
};

const removeLegacyTreatmentStorage = () => {
  [TREATMENT_STORAGE_KEY.runtimeSession, TREATMENT_STORAGE_KEY.checkoutResume]
    .forEach((key) => {
      localStorage.removeItem(key);
      sessionStorage.removeItem(key);
    });
};

const readLegacyTreatmentAuthority = () => {
  const candidates = [
    [sessionStorage, TREATMENT_STORAGE_KEY.runtimeSession],
    [localStorage, TREATMENT_STORAGE_KEY.runtimeSession],
    [sessionStorage, TREATMENT_STORAGE_KEY.checkoutResume],
    [localStorage, TREATMENT_STORAGE_KEY.checkoutResume],
  ];
  for (const [storage, key] of candidates) {
    const saved = parseStorage(storage, key);
    if (!saved.present) continue;
    if (saved.invalid || !saved.value || typeof saved.value !== "object") {
      throw new TreatmentRuntimeResumeError(
        "runtime_pointer_invalid",
        "Saved treatment progress is invalid. Start over to continue.",
      );
    }
    return saved.value;
  }
  return null;
};

export const readTreatmentRuntimePointer = () => {
  const saved = parseStorage(
    sessionStorage,
    TREATMENT_STORAGE_KEY.runtimePointer,
  );
  if (!saved.present) return null;
  if (
    saved.invalid
    || !saved.value
    || typeof saved.value !== "object"
    || saved.value.schema_version !== TREATMENT_RUNTIME_POINTER_SCHEMA_VERSION
    || !saved.value.session_token
  ) {
    throw new TreatmentRuntimeResumeError(
      "runtime_pointer_invalid",
      "Saved treatment progress is invalid. Start over to continue.",
    );
  }
  return saved.value;
};

export const clearTreatmentAuthority = () => {
  sessionStorage.removeItem(TREATMENT_STORAGE_KEY.runtimePointer);
  removeLegacyTreatmentStorage();
};

export const persistTreatmentAuthority = (session, context = {}) => {
  if (!session || session.mode === "preview") return;
  const normalizedSession = normalizeRuntimeAuthority(session);
  const existing = readTreatmentRuntimePointer();
  const sourceKind = String(
    context.sourceKind
    || normalizedSession.source_kind
    || normalizedSession.release?.source_kind
    || existing?.source_kind
    || "",
  );
  const sourceId = String(
    context.sourceId
    || normalizedSession.source_id
    || normalizedSession.release?.source_id
    || existing?.source_id
    || "",
  );
  if (!sourceKind || !sourceId || !session.session_token) {
    throw new TreatmentRuntimeResumeError(
      "runtime_pointer_invalid",
      "Treatment session identity is incomplete.",
    );
  }
  const pointer = {
    schema_version: TREATMENT_RUNTIME_POINTER_SCHEMA_VERSION,
    mode: sourceKind,
    runtime_api_base_url: normalizeRuntimeApiBaseUrl(
      context.apiBaseUrl
      || normalizedSession.runtime_api_base_url
      || existing?.runtime_api_base_url,
    ),
    source_kind: sourceKind,
    source_id: sourceId,
    release_id: normalizedSession.release?.id || existing?.release_id || null,
    release_version: (
      normalizedSession.release?.version ?? existing?.release_version ?? null
    ),
    release_checksum: (
      normalizedSession.release?.checksum || existing?.release_checksum || null
    ),
    session_token: normalizedSession.session_token,
    last_known_revision: normalizedSession.session_revision,
    current_node_id: (
      normalizedSession.cursor_node_id
      || context.currentItemId
      || existing?.current_node_id
      || null
    ),
    patient_ref: normalizedSession.patient_ref || existing?.patient_ref || null,
    // The server reports `patient_bound`; keep it so a later resume knows the
    // session cannot be read without that patient's token. Once bound, always bound.
    ...(normalizedSession.patient_bound === true || existing?.patient_bound === true
      ? { patient_bound: true }
      : {}),
    // Only the states a launch for another program must ask about; lets that
    // check work for a bound session that cannot be read without a token.
    ...askFirstStateHint(normalizedSession.state),
  };
  sessionStorage.setItem(
    TREATMENT_STORAGE_KEY.runtimePointer,
    JSON.stringify(pointer),
  );
  removeLegacyTreatmentStorage();
  return pointer;
};

export const persistTreatmentCheckoutResume = (session, context = {}) => {
  if (!session || session.mode === "preview") return;
  return persistTreatmentAuthority(
    {
      ...session,
      session_token: context.sessionToken || session.session_token,
      session_revision: context.sessionRevision || session.session_revision,
    },
    context,
  );
};

const sameRelease = (session, release) => (
  String(session?.release?.id || "") === String(release?.id || "")
  && Number(session?.release?.version || 0) === Number(release?.version || 0)
  && String(session?.release?.checksum || "") === String(release?.checksum || "")
);

/**
 * Fold a freshly restored projection over the answers already on screen.
 *
 * The runtime only persists a treatment's program answers once that whole set
 * submits, and consents once they are accepted, so a projection restored mid
 * treatment carries nothing for questions the patient has already filled in.
 * Replacing outright would throw that work away and leave the runtime asking
 * for answers the patient believes they gave.
 *
 * Server values win wherever the projection has one -- it stays authoritative,
 * including for answers it deliberately invalidated and re-sent as empty. A
 * local answer survives only where the projection is silent.
 */
export const mergeAuthorityResponses = (current, restored) => {
  const local = current && typeof current === "object" ? current : {};
  const authoritative = restored && typeof restored === "object" ? restored : {};
  return { ...local, ...authoritative };
};

const locationField = (question) => {
  const mapping = String(question?.beluga_field_mapping || "").toLowerCase();
  const text = String(question?.question_text || question?.label || "").toLowerCase();
  const identity = `${mapping} ${text}`;
  if (/zip|postal/.test(identity)) return "zip_code";
  if (/country/.test(identity)) return "country";
  if (/city/.test(identity)) return "city";
  if (/state/.test(identity)) return "state";
  if (/apartment|suite|unit|address_?2|street_?2/.test(identity)) return "street_2";
  if (/street|address/.test(identity)) return "street_1";
  return null;
};

const locationResponse = (question, location) => {
  if (!location || typeof location !== "object") return null;
  const response = {
    street_1: location.street_1 || location.address || "",
    street_2: location.street_2 || location.address_line_2 || "",
    city: location.city || "",
    state: location.state || "",
    zip_code: location.zip_code || location.zip || "",
    country: location.country || "US",
  };
  (question.sub_questions || []).forEach((subQuestion) => {
    const field = locationField(subQuestion);
    if (field) response[subQuestion.id] = response[field];
  });
  return response.street_1 && response.zip_code ? response : null;
};

const consentChoiceText = (choice) => (
  typeof choice === "string"
    ? choice
    : choice?.text || choice?.label || choice?.value || ""
);

const restoredConsentResponse = (question, snapshot) => {
  const text = snapshot?.selected_text
    || snapshot?.selected_value
    || (question.answer_choices || [])
      .map(consentChoiceText)
      .find((choice) => choice);
  if (!text) return true;
  return {
    agreed: snapshot.accepted === true,
    consent_text: text,
    ...(snapshot.selected_value
      ? { selected_value: snapshot.selected_value }
      : {}),
  };
};

export const authorityResponses = (questionnaire, session) => {
  const saved = session?.saved_answers || {};
  const responses = {};
  responses[TREATMENT_AUTHORITY_RESPONSE_KEY.recommendedProgramIds] = (
    session?.matching?.recommended_programs || []
  );
  const selectedOptions = saved.selected_options || [];
  for (const question of questionnaire?.questions || []) {
    const sourceId = String(question.source_id || question.id || "");
    const programId = String(question.program_id || "");
    let restored;
    if (programId && saved.programs?.[programId]) {
      restored = saved.programs[programId][sourceId];
    } else if (
      Object.prototype.hasOwnProperty.call(saved.common || {}, sourceId)
    ) {
      restored = saved.common[sourceId];
    } else if (
      Object.prototype.hasOwnProperty.call(saved.routing || {}, sourceId)
    ) {
      restored = saved.routing[sourceId];
    }
    if (restored !== undefined) {
      responses[question.id] = restored;
    }

    if (
      question.question_type === "shipping_address"
      && question.location_role !== "shipping"
    ) {
      const restoredLocation = locationResponse(question, session?.location_context);
      if (restoredLocation) responses[question.id] = restoredLocation;
    }

    if (
      question.question_type === "program_authentication_preview"
      && session?.patient_bound
    ) {
      responses[question.id] = {
        completed: true,
        resumed: true,
      };
    } else if (question.question_type === "matched_program_selection") {
      responses[question.id] = (
        question.matching_config?.stage === "recommendations"
          ? session?.matching?.recommended_programs
          : session?.matching?.selected_programs
      ) || [];
    } else if (
      isCanonicalConsentQuestion(question)
    ) {
      const nodeId = String(question.release_node_id || "");
      const snapshot = nodeId ? saved.consents?.[nodeId] : null;
      if (snapshot?.accepted) {
        responses[question.id] = restoredConsentResponse(question, snapshot);
      }
    } else if (question.question_type === "product_selection") {
      const validIds = new Set(
        (question.answer_choices || question.checkout_config?.products || [])
          .map((choice) => String(
            choice?.option_id || "",
          ))
          .filter(Boolean),
      );
      const selected = selectedOptions
        .filter((option) => validIds.has(String(option?.option_id || "")))
        .map((option) => ({
          option_id: String(option.option_id),
          quantity: option.quantity,
        }));
      if (selected.length) responses[question.id] = selected;
    } else if (question.question_type === "program_lab_checkout") {
      const restoredLabCheckout = saved.lab_checkout;
      if (
        restoredLabCheckout
        && typeof restoredLabCheckout === "object"
        && Object.keys(restoredLabCheckout).length > 0
      ) {
        responses[question.id] = session?.state === "lab_selection"
          ? resumeProgramLabCheckout(restoredLabCheckout, session.step)
          : restoredLabCheckout;
      }
    }
  }
  return responses;
};

// A transition can advance server authority while the browser still owns
// answers for later questions that have not reached their submission
// boundary. Preserve those pending answers, but always let server-confirmed
// values win when both sides contain the same question.
export const mergePendingAuthorityResponses = (
  pendingResponses,
  authoritativeResponses,
) => ({
  ...(pendingResponses || {}),
  ...(authoritativeResponses || {}),
});

/**
 * Reconcile a server projection without discarding answers that have not yet
 * reached their runtime submission boundary. Server-confirmed values always
 * win for duplicate question IDs.
 */
export const reconcileAuthorityResponses = ({
  questionnaire,
  session,
  pendingResponses,
  preservePendingResponses = false,
}) => {
  const authoritative = authorityResponses(questionnaire, session);
  const reconciled = preservePendingResponses
    ? mergePendingAuthorityResponses(pendingResponses, authoritative)
    : authoritative;
  const projected = pruneResponsesToProjection(
    reconciled,
    questionnaire,
    session,
  );
  if (session?.state !== "product_selection") return projected;

  const questions = questionnaire?.questions || [];
  const projectedProducts = projectRuntimeQuestionnaireQuestions(
    questions,
    session,
  ).filter((question) => question.question_type === "product_selection");
  const eligibleOptionsByQuestion = new Map(
    projectedProducts.map((question) => [
      String(question.id),
      new Set((question.answer_choices || []).map(
        (choice) => String(choice?.option_id || ""),
      ).filter(Boolean)),
    ]),
  );
  const next = { ...projected };
  questions
    .filter((question) => question.question_type === "product_selection")
    .forEach((question) => {
      const eligibleOptionIds = eligibleOptionsByQuestion.get(String(question.id));
      if (!eligibleOptionIds) {
        delete next[question.id];
        return;
      }
      const values = Array.isArray(next[question.id])
        ? next[question.id]
        : next[question.id]
          ? [next[question.id]]
          : [];
      const eligibleValues = values.filter((value) => eligibleOptionIds.has(String(
        value?.option_id || value?.optionId || "",
      )));
      if (eligibleValues.length) next[question.id] = eligibleValues;
      else delete next[question.id];
    });
  return next;
};

/**
 * Map a failed resume read to the error the caller throws. A session the
 * server no longer has ends the saved pointer; anything else stays retryable.
 */
const resumeFailure = (error) => {
  const terminalSessionError = (
    (typeof isTerminalTreatmentSessionError === "function"
      && isTerminalTreatmentSessionError(error))
    || [404, 410].includes(Number(error?.status))
    || [
      "runtime_session_expired",
      "runtime_session_not_found",
      "runtime_session_invalid",
      "session_expired",
      "session_not_found",
      "session_invalid",
    ].includes(error?.code)
  );
  if (terminalSessionError) {
    clearTreatmentAuthority();
    return new TreatmentRuntimeResumeError(
      "runtime_session_expired",
      "This treatment session is no longer available. Start over to continue.",
      {
        cause_code: error?.code || error?.rawCode || null,
        status: error?.status || null,
      },
    );
  }
  // An HTTP 4xx (408 aside) cannot change on retry, so the message does not suggest one.
  const status = Number(error?.status);
  const cannotRetry = status >= 400 && status < 500 && status !== 408;
  return new TreatmentRuntimeResumeError(
    "runtime_resume_failed",
    cannotRetry
      ? "Saved treatment progress could not be restored. Start over to continue."
      : "Saved treatment progress could not be restored. Retry or start over.",
    {
      cause_code: error?.code || error?.rawCode || null,
      status: error?.status || null,
      retryable: error?.retryable ?? true,
    },
  );
};

/**
 * The saved session is bound to a patient and this tab sent no usable patient
 * token. Try the refresh cookie once; if the patient still cannot read the
 * session, only signing in can change that, so the saved pointer is kept and
 * the caller gets a non-retryable sign-in error rather than an outage.
 */
const resumeAfterPatientRefresh = async (savedRuntime) => {
  try {
    await refreshPatientAccessToken(savedRuntime.runtime_api_base_url);
    return await getTreatmentSession(
      savedRuntime.session_token,
      null,
      savedRuntime.runtime_api_base_url,
    );
  } catch (error) {
    // The refresh helper reports every failed refresh as "session expired" but keeps
    // the HTTP status. A 5xx means the server is down, not that the login is gone.
    const loginIsGone = (
      error?.code === "patient_session_expired"
      && !(Number(error?.status) >= 500)
    );
    if (isRuntimePatientPermissionError(error) || loginIsGone) {
      throw new TreatmentRuntimeResumeError(
        RUNTIME_PATIENT_AUTH_REQUIRED,
        "Please sign in again to continue.",
        { status: 403 },
      );
    }
    throw resumeFailure(error);
  }
};

export const loadTreatmentAuthority = async ({
  sourceKind,
  sourceId,
  release,
  apiBaseUrl,
  runtimeSessionToken = null,
}) => {
  const normalizedApiBaseUrl = normalizeRuntimeApiBaseUrl(apiBaseUrl);
  let savedRuntime = runtimeSessionToken
    ? {
        mode: sourceKind,
        source_kind: sourceKind,
        source_id: String(sourceId || ""),
        runtime_api_base_url: normalizedApiBaseUrl,
        session_token: runtimeSessionToken,
      }
    : readTreatmentRuntimePointer();
  const legacyRuntime = savedRuntime ? null : readLegacyTreatmentAuthority();
  if (!savedRuntime && legacyRuntime) {
    savedRuntime = {
      schema_version: TREATMENT_RUNTIME_POINTER_SCHEMA_VERSION,
      mode: sourceKind,
      runtime_api_base_url: normalizedApiBaseUrl,
      source_kind: legacyRuntime.sourceKind || sourceKind,
      source_id: String(legacyRuntime.sourceId || sourceId || ""),
      release_id: legacyRuntime.releaseId || null,
      release_version: legacyRuntime.releaseVersion ?? null,
      release_checksum: legacyRuntime.releaseChecksum || null,
      session_token: legacyRuntime.sessionToken,
      last_known_revision: legacyRuntime.sessionRevision ?? null,
      current_node_id: legacyRuntime.currentItemId || null,
      patient_ref: null,
    };
  }

  // A pointer for another program is parked, not an error (WT-231).
  savedRuntime = await settleLaunchPointer(
    savedRuntime,
    { sourceKind, sourceId, apiBaseUrl: normalizedApiBaseUrl },
  );

  let resumed = null;
  if (savedRuntime) {
    if (!savedRuntime.session_token) {
      throw new TreatmentRuntimeResumeError(
        "runtime_pointer_invalid",
        "Saved treatment progress is invalid. Start over to continue.",
      );
    }
    if (
      savedRuntime.patient_bound === true
      && !sessionStorage.getItem(TREATMENT_STORAGE_KEY.patientAccessToken)
    ) {
      // A bound session cannot be read without its patient's token, so skip the
      // request that is certain to be refused and recover the login first.
      resumed = await resumeAfterPatientRefresh(savedRuntime);
    } else {
      try {
        resumed = await getTreatmentSession(
          savedRuntime.session_token,
          null,
          savedRuntime.runtime_api_base_url,
        );
      } catch (error) {
        if (!isRuntimePatientPermissionError(error)) {
          throw resumeFailure(error);
        }
        resumed = await resumeAfterPatientRefresh(savedRuntime);
      }
    }
    if (!sameRelease(resumed, release)) {
      throw new TreatmentRuntimeResumeError(
        "release_integrity_mismatch",
        "Saved treatment progress does not match this published release.",
      );
    }
    if (
      (savedRuntime.release_id
        && String(savedRuntime.release_id) !== String(resumed.release?.id || ""))
      || (savedRuntime.release_version != null
        && Number(savedRuntime.release_version)
          !== Number(resumed.release?.version || 0))
      || (savedRuntime.release_checksum
        && String(savedRuntime.release_checksum)
          !== String(resumed.release?.checksum || ""))
    ) {
      throw new TreatmentRuntimeResumeError(
        "release_integrity_mismatch",
        "Saved treatment progress does not match its pinned release.",
      );
    }
  }

  const session = resumed || await startTreatmentAuthority({
    sourceKind,
    sourceId,
    release,
    apiBaseUrl: normalizedApiBaseUrl,
  });
  persistTreatmentAuthority(session, {
    sourceKind,
    sourceId,
    apiBaseUrl: normalizedApiBaseUrl,
    currentItemId: savedRuntime?.current_node_id || null,
  });
  return {
    savedCheckout: null,
    savedRuntime,
    resumed,
    session,
    created: !resumed,
  };
};
